import React from 'react'
import PropTypes from 'prop-types'
import { Check } from 'lucide-react'
import { cn } from '../../lib/utils'

function Stepper({ steps, currentStep, onStepClick, className }) {
  return (
    <nav aria-label="Progress" className={cn('w-full', className)}>
      <ol className="flex items-center justify-between md:justify-center md:space-x-8">
        {steps.map((step, index) => {
          const stepNumber = index + 1
          const isCompleted = currentStep > stepNumber
          const isCurrent = currentStep === stepNumber
          const isUpcoming = currentStep < stepNumber
          const isClickable = (isCompleted || isCurrent) && onStepClick

          return (
            <li key={step.id} className="relative flex-1 md:flex-initial">
              <div className="group flex items-center">
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick(stepNumber, step.id)}
                  disabled={!isClickable}
                  className={cn(
                    'flex items-center px-2 md:px-6 py-4 text-sm font-medium transition-opacity',
                    isClickable && 'cursor-pointer hover:opacity-80',
                    !isClickable && 'cursor-default'
                  )}
                >
                  <span
                    className={cn(
                      'flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full transition-colors',
                      isCompleted && 'bg-purple-600',
                      isCurrent && 'border-2 border-purple-600 bg-white',
                      isUpcoming && 'border-2 border-gray-300 bg-white'
                    )}
                  >
                    {isCompleted ? (
                      <Check className="w-6 h-6 text-white" />
                    ) : (
                      <span
                        className={cn(
                          'transition-colors',
                          isCurrent && 'text-purple-600',
                          isUpcoming && 'text-gray-500'
                        )}
                      >
                        {stepNumber}
                      </span>
                    )}
                  </span>
                  <span
                    className={cn(
                      'ml-2 md:ml-4 text-sm font-medium transition-colors hidden md:inline',
                      isCompleted && 'text-gray-900',
                      isCurrent && 'text-purple-600',
                      isUpcoming && 'text-gray-500'
                    )}
                  >
                    {step.label}
                  </span>
                </button>
              </div>
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-1/2 left-full w-8 -ml-2" aria-hidden="true">
                  <div
                    className={cn(
                      'h-0.5 transition-colors',
                      currentStep > stepNumber ? 'bg-purple-600' : 'bg-gray-300'
                    )}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ol>
      <div className="mt-4 md:hidden">
        <div className="text-sm text-center text-gray-600 mb-2">
          Step {currentStep} of {steps.length}: {steps[currentStep - 1]?.label}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </nav>
  )
}

Stepper.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  currentStep: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
  className: PropTypes.string
}

export default Stepper
